import React, { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiList, PiMagnifyingGlass } from 'react-icons/pi'
import { NotificationButton } from 'components/Notification/NotificationButton'
import { useAuth } from 'context/authentication/useAuth'
import { useLeftNavStore } from 'components/Layout/LeftNav'
import { useLayoutConfig } from 'context/layout'
import { Z_INDEX } from 'styles/z-index'
import { Breadcrumbs } from './Breadcrumbs'
import {
  getActiveNavSection,
  getNavItem,
  resolveChildren,
  isNavItemEnabled,
  resolvePath,
} from 'components/Layout/LeftNav/useNavItems'
import { useNavItems } from 'components/Layout/LeftNav/NavItemsContext'
import { featureFlagService } from 'utils/featureFlagService'
import type { BreadcrumbItem } from './types'
import type { NavChildItem } from 'components/Layout/LeftNav/types'
import type { FeatureFlags } from 'utils/featureFlagService'
import type { User } from 'models/user'
import logoWhite from 'assets/Name - White Transparent.svg'
import apiService from 'services/api'
import { API_HOST } from 'utils/http-client'

// ---------------------------------------------------------------------------
// Generic breadcrumb builder
// ---------------------------------------------------------------------------

/**
 * Find the active first-level child by checking sub-children paths first
 * (for nested items), then falling back to the child's own path.
 */
function findActiveChild(
  children: NavChildItem[],
  pathname: string
): NavChildItem | undefined {
  return children.find((child) => {
    if (child.children?.length) {
      return child.children.some((sub) =>
        pathname.startsWith(resolvePath(sub.path))
      )
    }
    return pathname.startsWith(resolvePath(child.path))
  })
}

/**
 * Build breadcrumb dropdowns from nav children.
 *
 * - Always creates a first-level dropdown for the children.
 * - If the active child has `showChildrenBreadcrumb: true` and nested children,
 *   a second-level dropdown is added automatically.
 */
function buildBreadcrumbs(
  children: NavChildItem[],
  pathname: string,
  featureFlags: FeatureFlags,
  user?: User
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []

  const filtered = children.filter((child) =>
    isNavItemEnabled(child, featureFlags, user)
  )

  const activeChild = findActiveChild(filtered, pathname)

  // First dropdown: group/category level
  breadcrumbs.push({
    label: activeChild?.label || filtered[0]?.label || '',
    hasDropdown: true,
    dropdownItems: filtered.map((child) => {
      const firstVisibleSub = child.children?.filter((sub) =>
        isNavItemEnabled(sub, featureFlags, user)
      )?.[0]

      return {
        label: child.label,
        description: child.description,
        path: firstVisibleSub
          ? resolvePath(firstVisibleSub.path)
          : resolvePath(child.path),
        isActive: child === activeChild,
      }
    }),
  })

  // Second dropdown: nested children (opt-in via showChildrenBreadcrumb)
  if (activeChild?.showChildrenBreadcrumb && activeChild.children?.length) {
    const filteredSubs = activeChild.children.filter((sub) =>
      isNavItemEnabled(sub, featureFlags, user)
    )
    const activeSub = filteredSubs.find((sub) =>
      pathname.startsWith(resolvePath(sub.path))
    )

    breadcrumbs.push({
      label: activeSub?.label || filteredSubs[0]?.label || '',
      hasDropdown: true,
      dropdownItems: filteredSubs.map((sub) => ({
        label: sub.label,
        path: resolvePath(sub.path),
        isActive: sub === activeSub,
      })),
    })
  }

  return breadcrumbs
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface HeaderProps {
  onSearchClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({
  onSearchClick,
}): React.ReactElement => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAllowed } = useAuth()
  const { isCollapsed, toggleCollapsed } = useLeftNavStore()
  const { config } = useLayoutConfig()
  const api = apiService()
  const featureFlags = featureFlagService()
  const { navItems } = useNavItems()

  // Fetch company details to get the logo
  const { data: companyData } = api.useGetCompanyDetails()

  // Get the primary company logo if available
  const primaryLogoUrl = useMemo(() => {
    if (!companyData?.logos) return null

    const primaryLogo = companyData.logos.find((logo) => logo.is_primary)
    if (!primaryLogo?.logo_url) return null

    try {
      return new URL(primaryLogo.logo_url, API_HOST).href
    } catch {
      return null
    }
  }, [companyData])

  // Build breadcrumbs from nav items
  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const path = location.pathname
    const breadcrumbs: BreadcrumbItem[] = []

    // Entity pages set categoryInfo via layout config — use it to build a
    // synthetic path that resolves to the correct nav section generically.
    const effectivePath = config.categoryInfo
      ? `/targets/${config.categoryInfo.categorySlug}/${config.categoryInfo.subCategorySlug}`
      : path

    const activeSection = getActiveNavSection(effectivePath, navItems)
    if (!activeSection) return breadcrumbs

    const navItem = getNavItem(navItems, activeSection)
    if (!navItem) return breadcrumbs

    // Section label
    breadcrumbs.push({
      label: navItem.label.toUpperCase(),
      isSectionLabel: true,
    })

    const children = resolveChildren(navItem)
    if (children?.length) {
      if (children.length > 1) {
        breadcrumbs.push(
          ...buildBreadcrumbs(children, effectivePath, featureFlags, user)
        )
      } else {
        breadcrumbs.push({
          label: children[0].label,
          path: resolvePath(children[0].path),
        })
      }
    }

    // Entity pages append chain/door name breadcrumbs
    if (config.entityBreadcrumbs?.length) {
      breadcrumbs.push(...config.entityBreadcrumbs)
    }

    return breadcrumbs
  }, [
    location.pathname,
    navItems,
    featureFlags,
    user,
    config.categoryInfo,
    config.entityBreadcrumbs,
  ])

  const handleMenuToggle = useCallback(() => {
    toggleCollapsed()
  }, [toggleCollapsed])

  const handleSearchClick = useCallback(() => {
    if (onSearchClick) {
      onSearchClick()
    }
  }, [onSearchClick])

  const handleAvatarClick = useCallback(() => {
    if (isAllowed('Standard')) {
      navigate('/settings/users/users')
    }
  }, [navigate, isAllowed])

  // Get user initials for avatar fallback
  const userInitials = useMemo(() => {
    if (!user?.first_name && !user?.last_name) return '?'
    const first = user.first_name?.charAt(0) || ''
    const last = user.last_name?.charAt(0) || ''
    return (first + last).toUpperCase()
  }, [user])

  return (
    <header
      className="flex items-center h-[var(--header-height)] min-h-[var(--header-height)] bg-[var(--nav-bg-primary)] pr-6 relative"
      style={{ zIndex: Z_INDEX.NAV }}
    >
      {/* Menu Toggle Button */}
      <button
        onClick={handleMenuToggle}
        aria-label="Toggle navigation menu"
        aria-expanded={!isCollapsed}
        className="flex items-center justify-center w-[var(--left-nav-width)] h-full border-none bg-transparent text-[var(--nav-text-muted)] cursor-pointer transition-all duration-150 hover:text-[var(--nav-text-primary)] hover:bg-[var(--nav-bg-hover)]"
      >
        <PiList size={24} />
      </button>

      {/* Logo */}
      <div className="flex items-center justify-center py-[18px]">
        <img src={logoWhite} alt="First Bite" className="h-[45px]" />
      </div>

      {/* Breadcrumbs */}
      <div className="flex-1 flex items-center px-6 min-w-0">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Search Bar */}
        <button
          onClick={handleSearchClick}
          aria-label="Search First Bite"
          className="flex items-center gap-2 w-[220px] h-8 px-2 py-1 bg-[#e5ece7] border-none rounded-lg text-[#6e6e6e] text-[12px] cursor-pointer transition-shadow duration-150 shadow-[0px_6px_58px_0px_rgba(196,203,214,0.1)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] max-[1280px]:w-8 max-[1280px]:px-1 max-[1280px]:justify-center"
        >
          <PiMagnifyingGlass
            size={16}
            className="text-[#6e6e6e] flex-shrink-0"
          />
          <span className="flex-1 text-left max-[1280px]:hidden">
            Search First Bite
          </span>
        </button>

        {/* Notification Button */}
        <NotificationButton />

        {/* Avatar Button */}
        <button
          onClick={handleAvatarClick}
          aria-label="User profile"
          className="flex items-center justify-center w-8 h-8 p-0 border-none rounded-lg bg-[var(--nav-bg-hover)] text-[var(--nav-text-primary)] text-[14px] font-medium cursor-pointer overflow-hidden transition-opacity duration-150 hover:opacity-90"
        >
          {primaryLogoUrl ? (
            <img
              src={primaryLogoUrl}
              alt="Company logo"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            userInitials
          )}
        </button>
      </div>
    </header>
  )
}
